import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import api from "../api";

export default function Profile() {
  const { currentUser, isContractor, refreshContractorStatus } = useAuth();
  // 保存で区分（社員／業務委託）が変わったときの案内。freee_id の有無で区分が決まる（issue #85）。
  const [contractorNotice, setContractorNotice] = useState("");
  const encodedEmail = encodeURIComponent(currentUser.email);
  const [freeeId, setFreeeId] = useState("");
  const [slackStampId, setSlackStampId] = useState("");
  const [defaultStartHour, setDefaultStartHour] = useState(""); // デフォルトの開始時間（時間）を管理
  const [defaultStartMinute, setDefaultStartMinute] = useState("");
  const [freeeAccessToken, setFreeeAccessToken] = useState(""); // freee_access_tokenの状態を管理
  const [freeeRefreshToken, setFreeeRefreshToken] = useState(""); // freee_refresh_tokenの状態を管理
  const [savedUserInfo, setSavedUserInfo] = useState("");
  const [loading, setLoading] = useState(false); // ローディング状態を管理

  useEffect(() => {
    // ページが読み込まれた時に、サーバーからSlack IDを取得する処理
    const fetchUserInfo = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/users/${encodedEmail}`,
        );
        if (response.status === 200) {
          const data = await response.data;
          if (Object.keys(data).length === 0) {
            return;
          }
          console.log("User Info:", data);
          setSlackStampId(data.slack_stamp_id || "");
          setFreeeId(data.freee_id || "");
          const [hour, minute] = (data.default_start_time || "00:00").split(
            ":",
          );
          setDefaultStartHour(hour);
          setDefaultStartMinute(minute);
          setFreeeAccessToken(data.freee_access_token || "");
          setFreeeRefreshToken(data.freee_refresh_token || "");
        } else {
          console.error("ユーザ情報の取得に失敗しました");
        }
      } catch (error) {
        console.error("エラーが発生しました:", error);
      }
    };
    fetchUserInfo();
  }, [encodedEmail]);

  const handleSave = async () => {
    setLoading(true); // ローディング状態を開始
    const defaultStartTime = `${defaultStartHour}:${defaultStartMinute}`;
    const payload = {
      email: encodedEmail,
      freee_id: freeeId,
      slack_stamp_id: slackStampId,
      default_start_time: defaultStartTime,
      freee_access_token: freeeAccessToken, // freee_access_tokenを追加
      freee_refresh_token: freeeRefreshToken, // freee_refresh_tokenを追加
    };
    // サーバーにSlack IDを保存する処理
    try {
      const response = await api.put(
        `${process.env.REACT_APP_API_URL}/users/${encodedEmail}`,
        payload,
      );
      if (response.status === 200) {
        console.log("ユーザ情報が正常に保存されました");
        setSavedUserInfo(payload);
        // 取り直さないと、勤怠登録が古い区分のエンドポイントに送られて 403 になる。
        const wasContractor = isContractor;
        const nowContractor = await refreshContractorStatus();
        if (nowContractor !== null && nowContractor !== wasContractor) {
          setContractorNotice(
            nowContractor
              ? "freee IDが未設定のため、業務委託者として扱われます。"
              : "freee IDが設定されたため、社員として扱われます。",
          );
        } else {
          setContractorNotice("");
        }
      } else {
        console.error("ユーザ情報の保存に失敗しました");
      }
    } catch (error) {
      console.error("エラーが発生しました:", error);
    } finally {
      setLoading(false); // ローディング状態を終了
    }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));

  return (
    <>
      <Container maxWidth="md">
        <Box
          display="flex"
          mt={4}
          mb={4}
          sx={{ "& .MuiTextField-root": { width: "20ch" } }}
        >
          <TextField
            label="Freee ID"
            variant="outlined"
            value={freeeId}
            size="small"
            onChange={(e) => setFreeeId(e.target.value)}
          />
        </Box>
        <Box
          display="flex"
          mt={4}
          mb={4}
          sx={{ "& .MuiTextField-root": { width: "20ch" } }}
        >
          <TextField
            label="Slack Stamp ID"
            variant="outlined"
            value={slackStampId}
            size="small"
            onChange={(e) => setSlackStampId(e.target.value)}
          />
        </Box>
        <Box
          display="flex"
          mt={4}
          mb={4}
          sx={{ "& .MuiTextField-root": { width: "100ch" } }}
        >
          <TextField
            label="Freee Access Token"
            variant="outlined"
            value={freeeAccessToken}
            size="small"
            onChange={(e) => setFreeeAccessToken(e.target.value)}
          />
        </Box>
        <Box
          display="flex"
          mt={4}
          mb={4}
          sx={{ "& .MuiTextField-root": { width: "100ch" } }}
        >
          <TextField
            label="Freee Refresh Token"
            variant="outlined"
            value={freeeRefreshToken}
            size="small"
            onChange={(e) => setFreeeRefreshToken(e.target.value)}
          />
        </Box>
        <FormControl variant="outlined" size="small" sx={{ mb: 2, width: 100 }}>
          <InputLabel>開始時間</InputLabel>
          <Select
            label="デフォルト開始時間（時）"
            value={defaultStartHour}
            onChange={(e) => setDefaultStartHour(e.target.value)}
          >
            {hours.map((hour) => (
              <MenuItem key={hour} value={hour}>
                {hour}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" sx={{ mb: 2, width: 50 }}>
          <InputLabel>分</InputLabel>
          <Select
            label="デフォルト開始時間（分）"
            value={defaultStartMinute}
            onChange={(e) => setDefaultStartMinute(e.target.value)}
          >
            {minutes.map((minute) => (
              <MenuItem key={minute} value={minute}>
                {minute}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading
              || (!freeeId
                && !slackStampId
                && !defaultStartHour
                && !defaultStartMinute
                && !freeeAccessToken
                && !freeeRefreshToken)} // 必須フィールドが入力されていないと保存ボタンを無効化
          >
            保存
          </Button>
          <Dialog open={loading}>
            <DialogContent>
              <CircularProgress />
            </DialogContent>
          </Dialog>
        </Box>
        {savedUserInfo && <Typography mt={2}>ユーザ情報が保存されました.</Typography>}
        {contractorNotice && <Typography mt={1}>{contractorNotice}</Typography>}
      </Container>
    </>
  );
}
